import { useEffect, useRef, useState } from "react";
import { useLocation, useMatch, useParams, useSearchParams } from "react-router-dom";
import { useI18nNavigate } from "@/hooks/use-i18n-navigate";
import {
  iamEmailManager,
  iamEmailSessionIdManager,
  iamAccountManager,
  iamTokenManager,
  routeLastVisitManager,
  routeLastVisitCreationManager,
  iamVerifyCodeManager,
  iamLoginMethodManager,
  iamInviteIamTokenManager,
  iamInviteIamEmailManager,
  iamTeamManager,
  subscriptionManager,
  agentManager,
} from "@/manager";
import { useLogout } from "@/hooks/use-logout";
import { showToastByCode } from "@/hooks/use-toast";
import { useAPIUsersAcceptInvitation } from "@/hooks/use-api-users-accept-invitaion";
import { logger } from "@/utils/logger";
import analytics from "@/utils/analytics";
import { ENV } from "@/constant";
import { resolveAgentEntryDestination } from "@/utils/agent-entry-route";
import { seedResolvedAgentFromSSR } from "@/utils/ssr-agent-resolve";

export function useApp() {
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useI18nNavigate();
  const { handleLogout } = useLogout();
  const { handleAPIUsersAcceptInvitation } = useAPIUsersAcceptInvitation();
  const isHome = useMatch("/");
  const isLogin = useMatch("/login/");
  const isSelectSystem = useMatch("/select-system/");
  const isInvite = useMatch("/invite/");
  // const isProduct = useMatch("/creation/product/");
  const isFolder = useMatch("/creation/folder/:id/");
  const isProject = useMatch("/creation/project/:id/");
  const isAccount = useMatch("/settings/account/");
  const isSubscription = useMatch("/settings/subscription/");
  const isMembers = useMatch("/settings/members/");
  const isMaterial = useMatch("/assets/material/");
  const isKnowledge = useMatch("/assets/knowledge/");
  const isMaterialItem = useMatch("/assets/material/:id/");
  const isKnowledgeItem = useMatch("/assets/knowledge/:id/");
  const isActor = useMatch("/assets/actor/");
  const isVoice = useMatch("/assets/voice/");
  const isAgent = useMatch("/agent/");
  const isUnSubscribe = useMatch("/unsubscribe/");
  const isAgentDashboard = useMatch("/agent/dashboard/");
  const isAgentUserManagement = useMatch("/agent/dashboard/user-management/:id/");
  const isAgentWorkflow = useMatch("/agent/dashboard/workflow/:id/");
  const isAgentSubscription = useMatch("/agent/dashboard/subscription-settings/:id/");
  const isAgentUserManagementDetail = useMatch("/agent/dashboard/user-management/:id/:userId");
  const isSubscriptionPlan = useMatch("/subscription-plan/:state?");
  const isAgentCreation = useMatch("/agent/creation/:id/");
  const isAgentDashboardPermission = useMatch("/agent/dashboard/permission/:id/");
  const isAgentDashboardDataOverview = useMatch("/agent/dashboard/data-overview/:id/");
  const isAgentDashboardContentManagement = useMatch("/agent/dashboard/content-management/:id/");
  const isAgentDashboardOrderManagement = useMatch("/agent/dashboard/order-management/:id/");
  const isAgentDashboardCompanyAsset = useMatch("/agent/dashboard/company-asset/:id/");
  const isAgentDashboardOperationLog = useMatch("/agent/dashboard/operation-log/:id/");

  const [loading, setLoading] = useState(true);
  const initRef = useRef(false);
  const timeRef = useRef<number>(0);

  const init = async () => {
    // 兜底再执行一次，确保在任何入口下都能复用 SSR 数据。
    const ssrAgentResolveData = seedResolvedAgentFromSSR();
    if (ssrAgentResolveData) {
      logger.info("useApp: SSR agent resolve data", ssrAgentResolveData);
    }

    const token = searchParams.get("token");
    const shareToken = searchParams.get("shareToken");
    const targetAgentId = searchParams.get("targetAgentId");
    if (targetAgentId) {
      agentManager.setAgentId(targetAgentId);
      agentManager.setAgentIdToCache(targetAgentId, window.location.origin);
    }
    if (token) {
      analytics.trackGoogleLoginSuccess();
      iamTokenManager.updateToken(token);
      await navigate("/", { replace: true });
      logger.info("useApp: google login");
    }
    // 站内跳转
    if (shareToken) {
      iamTokenManager.updateToken(shareToken);
      await navigate(location.pathname, { replace: true });
      iamAccountManager.init();
      logger.info("useApp: share token");
      return;
    }
    // 跳转到登录页但已登录
    if (isLogin && iamTokenManager.getToken()) {
      await navigate("/", { replace: true });
      logger.info("useApp: already login");
      return;
    }
    // 更新初始化状态
    initRef.current = true;
    logger.info("useApp: init");
    if (!iamTokenManager.getToken()) {
      await navigate("/login/");
      logger.info("useApp: token expired");
      return;
    }
    // 初始化智能体数据
    await agentManager.init();
    iamAccountManager.init();
    // iamTeamManager.init();

    // 是否是定制域名
    const isCustomDomain = ENV.isProdSubEnv || ENV.isTestSubEnv;

    const entryDest = resolveAgentEntryDestination({
      env: {
        isDevEnv: ENV.isDevEnv,
        isTestMainEnv: ENV.isTestMainEnv,
        isProdMainEnv: ENV.isProdMainEnv,
        isCustomDomain,
      },
      agents: agentManager.getAgents(),
      domainAgentId: agentManager.getAgentId(),
      domainAgentStatus: agentManager.getResolvedAgent()?.status
    });

    if (ENV.isDevEnv || ENV.isTestMainEnv) {
      if (entryDest.type === "select-system") {
        await navigate("/select-system/", { replace: true });
        logger.info("useApp: dev or test main -> select-system");
        return;
      }
    } else if (isCustomDomain) {
      if (entryDest.type === "resume-last-visit") {
        await navigate(await routeLastVisitManager.get(), { replace: true });
        logger.info("useApp: custom domain -> resume last visit");
      } else {
        await navigate("/select-system/", { replace: true });
        logger.info("useApp: custom domain -> select-system");
      }
      return;
    } else if (ENV.isProdMainEnv) {
      if (entryDest.type === "select-system") {
        await navigate("/select-system/", { replace: true });
        logger.info("useApp: prod main -> select-system (entry from home)");
      }
      return;
    }

    // 没有智能体数据跳转到未订阅页
    // if (!agentManager.isAgentData()) {
    //   await navigate("/unsubscribe/", { replace: true });
    //   return;
    // }

    // 订阅页刷新，如果有智能体，跳到第一个智能体详情页
    // if (isUnSubscribe) {
    //   if (agentManager.isAgentData()) {
    //     await navigate(`/agent/creation/${agentManager.getAgents()?.[0].id}/`, { replace: true });
    //     return;
    //   }
    // }

    // 【Tips：智能体二期需求，暂时关闭进入定价页判断】
    // // 非会员账号跳转到订阅套餐页，vipType：-1 不是会员   0 当前是会员   1 曾经是会员
    // if (iamAccountManager.get()?.vipType === VIP_TYPE.not) {
    //   await navigate("/subscription-plan/", { replace: Boolean(isSubscriptionPlan) });
    //   logger.info("useApp: navigate to subscription plan");
    //   return;
    // }
    // //  订阅套餐页面特殊处理：是订阅套餐页则不记录到最后一次访问路径
    // if (isSubscriptionPlan) {
    //   logger.info("useApp: visit subscription plan");
    //   return;
    // }
    // 是可访问路径则更新上次访问路径为该路径
    if (
      // isProject
      isAccount
      // || isMembers
      // || isSubscription
      || isActor
      || isKnowledge
      || isMaterial
      || isMaterialItem
      || isKnowledgeItem
      || isVoice
      || isAgent
      || isUnSubscribe
      || isAgentDashboard
      || isAgentUserManagement
      || isAgentWorkflow
      || isAgentSubscription
      || isAgentUserManagementDetail
      || isAgentCreation
      || isAgentDashboardPermission
      || isAgentDashboardDataOverview
      || isAgentDashboardContentManagement
      || isAgentDashboardOrderManagement
      || isAgentDashboardCompanyAsset
      || isAgentDashboardOperationLog
      || isSelectSystem
    ) {
      routeLastVisitManager.update(location.pathname + location.search);
      logger.info("useApp: update last visit path");
    }
    try {
      await navigate(await routeLastVisitManager.get(), { replace: true });
      logger.info("useApp: navigate to last visit path");
    } catch (error) {
      showToastByCode(error);
    }
  };

  // 上次访问路径记录
  useEffect(() => {
    if (isFolder
      // || isProject
      || isAccount
      // || isMembers
      // || isSubscription
      || isActor
      || isKnowledge
      || isMaterial
      || isMaterialItem
      || isKnowledgeItem
      || isVoice
      || isAgent
      // || isUnSubscribe
      || isAgentDashboard
      || isAgentUserManagement
      || isAgentWorkflow
      || isAgentSubscription
      || isAgentUserManagementDetail
      || isAgentCreation
      || isAgentDashboardPermission
      || isAgentDashboardDataOverview
      || isAgentDashboardContentManagement
      || isAgentDashboardOrderManagement
      || isAgentDashboardCompanyAsset
      || isAgentDashboardOperationLog
      || isSelectSystem
    ) {
      routeLastVisitManager.update(location.pathname + location.search);
    }
  }, [location.pathname]);

  // 上次访问创作区块路径记录
  useEffect(() => {
    if (isFolder || isProject) {
      routeLastVisitCreationManager.update(location.pathname);
    }
  }, [location.pathname]);

  // 上次访问资产路径记录
  useEffect(() => {
    if (isMaterial
      || isMaterialItem
      || isKnowledge
      || isKnowledgeItem
      || isVoice
      || isActor
    ) {
      routeLastVisitManager.updateLastVisitAssets(location.pathname);
    }
  }, [location.pathname]);

  // 每次路径变化都需要判断是否需要走初始化逻辑
  useEffect(() => {
    // 重新进入登录页或邀请码填写页或首页时重置初始化状态
    if (isLogin || isInvite || isHome) {
      initRef.current = false;
      logger.info("useApp: reset init state");
    }
    // 已初始化则不再初始化
    if (initRef.current) {
      logger.info("useApp: already init");
      return;
    }
    const time = Date.now();
    timeRef.current = time;
    setLoading(true);
    init().then(() => {
      if (time < timeRef.current) {
        return;
      }
      setLoading(false);
    });
  }, [location.pathname]);



  // 处理登录态失效
  useEffect(() => {
    return iamTokenManager.listen((e) => {
      if (!e.detail.token) {
        navigate("/login/");
      }
    });
  }, []);

  return {
    loading,
  };
}
